import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { g, p, api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { usePlan } from "@/context/PlanContext";
import { useI18n } from "@/i18n";
import { PremiumLock } from "@/components/PremiumLock";
import { DatiPersonali } from "@/components/DatiPersonali";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, Upload, ShieldAlert } from "lucide-react";

export default function Backup() {
  const { user, refresh } = useApp();
  const { has } = usePlan();
  const { t, lang } = useI18n();
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const file = useRef(null);

  const load = useCallback(async () => {
    if (!has("backup")) return;
    try {
      setInfo(await g("/backup/info"));
    } catch (e) {
      /* piano non abilitato */
    }
  }, [has]);
  useEffect(() => {
    load();
  }, [load]);

  if (!has("backup"))
    return (
      <div className="space-y-5">
        <PremiumLock
          title={t("x.bk.title")}
          description={t("x.bk.warn")}
          needed="pro"
          message={t("x.bk.title")}
          preview={
            <div className="p-6">
              <Card className="p-5">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{t("x.bk.content")}</p>
                <p className="mt-3 text-sm text-slate-500">menu · tavoli · staff · prenotazioni · comande · incassi · magazzino · sprechi</p>
              </Card>
            </div>
          }
        />
        {/* I diritti sui propri dati non dipendono dal piano: restano visibili anche col Gratuito */}
        <DatiPersonali />
      </div>
    );

  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get("/backup", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `reprehendo-backup-${new Date().toISOString().slice(0, 16).replace(/[:T]/g, "")}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t("x.bk.downloaded"));
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };

  const restore = async (f) => {
    setBusy(true);
    try {
      const dump = JSON.parse(await f.text());
      if (!dump?.data?.settings) throw new Error("bad");
      await p("/backup/restore", { data: dump.data, at: dump.at, by: user.name });
      toast.success(t("x.bk.restoreOk"));
      await refresh();
      load();
    } catch (e) {
      toast.error(e.message === "bad" ? t("x.bk.bad") : errMsg(e));
    }
    setBusy(false);
  };

  return (
    <div className="fade-up max-w-3xl space-y-5" data-testid="backup-page">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500">{t("x.bk.overline")}</p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">{t("x.bk.title")}</h1>
      </div>

      <Card className="p-6" data-testid="backup-actions">
        <p className="flex items-start gap-2 rounded-md bg-amber-50 p-3 text-xs text-amber-900">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" /> {t("x.bk.warn")}
        </p>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Button className="h-12" disabled={busy} onClick={download} data-testid="download-backup-btn">
            <Download className="mr-1.5 h-4 w-4" /> {t("x.bk.download")}
          </Button>
          <Button variant="outline" className="h-12" disabled={busy} onClick={() => file.current?.click()} data-testid="restore-backup-btn">
            <Upload className="mr-1.5 h-4 w-4" /> {t("x.bk.restore")}
          </Button>
        </div>
        <input
          ref={file}
          type="file"
          accept="application/json,.json"
          className="hidden"
          data-testid="backup-file-input"
          onChange={(e) => e.target.files?.[0] && restore(e.target.files[0])}
        />
      </Card>

      <Card className="p-6" data-testid="backup-content">
        <h3 className="text-lg font-semibold">{t("x.bk.content")}</h3>
        <div className="mt-3 grid grid-cols-2 gap-2 text-sm sm:grid-cols-3">
          {Object.entries(info?.counts || {}).map(([c, n]) => (
            <div key={c} className="rounded-md border border-slate-200 px-3 py-2" data-testid={`backup-coll-${c}`}>
              <p className="truncate text-[11px] font-semibold text-slate-600">{c}</p>
              <p className="font-num text-sm font-bold">{n} <span className="text-[10px] font-medium text-slate-400">{t("x.bk.records")}</span></p>
            </div>
          ))}
        </div>
      </Card>

      {(info?.restores || []).length > 0 && (
        <Card className="p-6" data-testid="backup-history">
          <h3 className="text-lg font-semibold">{t("x.bk.history")}</h3>
          <div className="mt-3 space-y-1 text-xs text-slate-500">
            {info.restores.map((r) => (
              <p key={r.id} data-testid={`restore-${r.id}`}>
                {new Date(r.at).toLocaleString(lang, { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })} · {r.by} ·{" "}
                {Object.values(r.restored || {}).reduce((a, b) => a + b, 0)} {t("x.bk.records")}
              </p>
            ))}
          </div>
        </Card>
      )}

      <DatiPersonali />
    </div>
  );
}
